import { supabase } from '../../../shared/lib/supabaseClient';
import { logActivity } from '../../../shared/services/activityLogService';
import { escapeLike } from '../../../shared/utils/escapeLike';

// One-to-one embeds come back as an object, but tolerate an array too.
const toAdvisory = (embed) => {
  const row = Array.isArray(embed) ? embed[0] : embed;
  return row ? { grade_level: row.grade_level, section: row.section } : null;
};

export async function getTeachers({ search = '', page = 1, pageSize = 10 } = {}) {
  let query = supabase
    .from('teachers')
    .select('*, advisory_sections(grade_level, section)', { count: 'exact' })
    .order('lastname', { ascending: true });

  if (search) {
    query = query.or(
      `firstname.ilike.%${search}%,lastname.ilike.%${search}%,username.ilike.%${search}%,email.ilike.%${search}%`
    );
  }

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const { data, error, count } = await query.range(from, to);
  if (error) throw error;
  return {
    data: data.map(({ advisory_sections, ...teacher }) => ({ ...teacher, advisory: toAdvisory(advisory_sections) })),
    count: count ?? 0,
  };
}

// The signed-in teacher's advisory section as { grade_level, section }, or null if they have none.
export async function getOwnAdvisory() {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!user?.email) return null;

  const { data, error } = await supabase
    .from('advisory_sections')
    .select('grade_level, section, teachers!inner(email)')
    .ilike('teachers.email', escapeLike(user.email))
    .maybeSingle();
  if (error) throw error;
  return data ? { grade_level: data.grade_level, section: data.section } : null;
}

export async function getOwnTeacher() {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!user?.email) return null;

  const { data, error } = await supabase
    .from('teachers')
    .select('firstname, lastname, middlename, username')
    .ilike('email', escapeLike(user.email))
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function getAllTeachers() {
  const { data, error } = await supabase
    .from('teachers')
    .select('id, firstname, lastname')
    .order('lastname', { ascending: true });
  if (error) throw error;
  return data;
}

// `advisory` is { grade_level, section } to assign, null to clear, or left out to leave it alone
// (e.g. when only toggling approval).
async function saveAdvisory(teacherId, advisory) {
  if (advisory === undefined) return;

  if (advisory === null) {
    const { error } = await supabase.from('advisory_sections').delete().eq('teacher_id', teacherId);
    if (error) throw error;
    return;
  }

  const { error } = await supabase
    .from('advisory_sections')
    .upsert({ teacher_id: teacherId, ...advisory }, { onConflict: 'teacher_id' });
  if (error) {
    if (error.code === '23505') throw new Error('That section already has an adviser.');
    throw error;
  }
}

// Teachers can't sign themselves up: a super admin adds them, and their login is created by the
// manage-teacher Edge Function (supabase/functions), which holds the service-role key.
async function manageTeacher(body) {
  const { data, error } = await supabase.functions.invoke('manage-teacher', { body });
  if (error) {
    // No usable answer: the network is down, the function isn't deployed, or it crashed (the
    // runtime's crash reply has no CORS headers, so the browser hides it). Its logs say which.
    if (error.name === 'FunctionsFetchError') {
      throw new Error(
        "Couldn't get an answer from the manage-teacher function. Try again; if it keeps failing, check its logs in the Supabase dashboard (Edge Functions → manage-teacher → Logs)."
      );
    }
    // A non-2xx reply carries the function's own message in its JSON body.
    const message = await error.context?.json?.().then((b) => b?.error).catch(() => null);
    throw new Error(message || error.message);
  }
  return data;
}

// Creates the teacher's login (username or employee ID + password) and their approved teachers row
// together.
export async function createTeacher({ advisory, password, username, ...teacher }) {
  const { teacher: created } = await manageTeacher({ action: 'create', username, password, ...teacher });
  await saveAdvisory(created.id, advisory);
  await logActivity('teacher_created', `Added teacher ${teacher.firstname} ${teacher.lastname}`);
  return created;
}

// `username` and `password` are optional: leave them out to keep the current ones. They change the
// teacher's login, so they go through the Edge Function, never a direct update.
export async function updateTeacher(id, { advisory, password, username, ...teacher }) {
  const { data, error } = await supabase.from('teachers').update(teacher).eq('id', id).select().single();
  if (error) throw error;
  await saveAdvisory(id, advisory);
  if (password || username) await manageTeacher({ action: 'update_login', teacher_id: id, username, password });
  if (teacher.firstname) {
    await logActivity(
      'teacher_updated',
      `Updated teacher ${teacher.firstname} ${teacher.lastname}${password ? ' (password reset)' : ''}`
    );
  }
  return data;
}

// Active teachers who haven't chosen their own login yet (must_change_login), for the super admin's
// notification bell: { count, names } with up to `limit` names.
export async function getMustChangeLoginTeachers({ limit = 5 } = {}) {
  const { data, error, count } = await supabase
    .from('teachers')
    .select('firstname, lastname', { count: 'exact' })
    .eq('approved', true)
    .eq('must_change_login', true)
    .not('user_id', 'is', null)
    .order('lastname', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return { count: count ?? 0, names: data.map((t) => `${t.firstname} ${t.lastname}`) };
}

// The signed-in teacher changes their own username and/or password (TeacherProfilePage), confirmed
// with their current password. A new username changes the login's email, which the access rules
// match on, so the session is refreshed to carry it.
export async function updateOwnLogin({ currentPassword, username, newPassword }) {
  await manageTeacher({
    action: 'update_own_login',
    current_password: currentPassword,
    username,
    password: newPassword || undefined,
  });
  const { error } = await supabase.auth.refreshSession();
  if (error) throw error;
  await logActivity('login_updated', newPassword ? 'Changed own login details' : 'Changed own username');
}

// Removes the teacher's row and their login.
export async function deleteTeacher(id, name) {
  await manageTeacher({ action: 'delete', teacher_id: id });
  await logActivity('teacher_deleted', `Removed teacher ${name}`);
}
